/**
 * MIDIClockManager - MIDI Clock Synchronization and Transport
 * Handles MIDI clock messages, calculates BPM, manages transport state.
 * Decoupled from UI - updates state instead of DOM.
 */
import { BPMTimingManager } from '../timing/BPMTimingManager.js';
import { MIDI_CONSTANTS } from './MIDIConstants.js';

export class MIDIClockManager {
  constructor(state) {
    this.state = state;
    this.isClockActive = false;
    this.clockPulses = 0;
    this.lastClockTime = 0;
    this.clockInterval = 0;
    this.bpm = MIDI_CONSTANTS.defaults.tempo;
    this.clockSource = 'internal';

    this.bpmTimingManager = new BPMTimingManager(this.bpm);

    this.bpmUpdateInterval = MIDI_CONSTANTS.clock.pulsesPerQuarterNote;
    this.bpmSamples = [];
    this.maxBpmSamples = 4;
    this.syncMode = 'auto';

    // Transport state
    this.isPlaying = false;
    this.isStopped = true;
    this.isPaused = false;

    // Sync points
    this.syncPoints = { quarter: 0, eighth: 0, sixteenth: 0, bar: 0 };

    // Animation time tracking (simple internal clock)
    this.animationTime = 0;
    this.lastFrameTime = 0;

    /** Monotonic tick count for BPM averaging (runs even when transport stopped). */
    this._bpmTickCount = 0;

    /**
     * Frozen quarter-note count while transport is stopped (not wall seconds — avoids phase
     * drift when BPM still updates from MIDI clock between frames).
     * @type {number | null}
     */
    this._musicalHoldBeats = null;

    // Tap tempo
    this.tapTimes = [];
  }

  onMIDIClock() {
    const now = performance.now();

    if (this.lastClockTime > 0) {
      this.clockInterval = now - this.lastClockTime;

      if (this.clockInterval > 0 && this.clockInterval < MIDI_CONSTANTS.clock.clockTimeout * 10) {
        const currentBpm = 60000 / (this.clockInterval * MIDI_CONSTANTS.clock.pulsesPerQuarterNote);

        if (currentBpm >= MIDI_CONSTANTS.clock.minTempo && currentBpm <= MIDI_CONSTANTS.clock.maxTempo && isFinite(currentBpm)) {
          this.bpmSamples.push(currentBpm);
          if (this.bpmSamples.length > this.maxBpmSamples) this.bpmSamples.shift();

          if (this._bpmTickCount % this.bpmUpdateInterval === 0) {
            const avgBpm = this.bpmSamples.reduce((s, b) => s + b, 0) / this.bpmSamples.length;
            if (Math.abs(avgBpm - this.bpm) > 0.5) {
              this.setBPM(avgBpm);
            }
          }
        }
      }
    }

    this.lastClockTime = now;
    this._bpmTickCount++;

    // Song position only advances while host transport is playing (Start/Continue).
    if (!this.isPlaying) {
      return;
    }

    this.clockPulses++;
    this.isClockActive = true;
    this.clockSource = 'external';

    this.updateSyncPoints();
  }

  onMIDIStart() {
    this._musicalHoldBeats = null;
    this.clockPulses = 0;
    this.isClockActive = true;
    this.clockSource = 'external';
    this.isPlaying = true;
    this.isStopped = false;
    this.isPaused = false;
    this.animationTime = 0;
    this._updateTransportState();
  }

  onMIDIStop() {
    if (this.clockSource === 'external') {
      const ppqn = MIDI_CONSTANTS.clock.pulsesPerQuarterNote;
      this._musicalHoldBeats = this.clockPulses / ppqn;
    }
    this.isClockActive = false;
    this.clockSource = 'internal';
    this.isPlaying = false;
    this.isStopped = true;
    this.isPaused = false;
    this._updateTransportState();
  }

  onMIDIContinue() {
    this._musicalHoldSeconds = null;
    this.isClockActive = true;
    this.clockSource = 'external';
    this.isPlaying = true;
    this.isStopped = false;
    this.isPaused = false;
    this._updateTransportState();
  }

  updateSyncPoints() {
    this.syncPoints.quarter = Math.floor(this.clockPulses / MIDI_CONSTANTS.clock.pulsesPerQuarterNote);
    this.syncPoints.eighth = Math.floor(this.clockPulses / MIDI_CONSTANTS.clock.pulsesPerEighthNote);
    this.syncPoints.sixteenth = Math.floor(this.clockPulses / MIDI_CONSTANTS.clock.pulsesPerSixteenthNote);
    this.syncPoints.bar = Math.floor(this.clockPulses / MIDI_CONSTANTS.clock.pulsesPerBar);
  }

  getBPM() { return this.bpm; }
  getBPMTimingManager() { return this.bpmTimingManager; }
  isExternalClockActive() { return this.isClockActive && this.clockSource === 'external'; }

  setBPM(bpm) {
    if (bpm >= MIDI_CONSTANTS.clock.minTempo && bpm <= MIDI_CONSTANTS.clock.maxTempo && isFinite(bpm)) {
      this.bpm = bpm;
      this.bpmTimingManager.setBPM(bpm);
      this.state.set('globalBPM', Math.round(bpm));
      this._updateBPMDisplay();
    }
  }

  increaseBPM() { this.setBPM(Math.min(MIDI_CONSTANTS.clock.maxTempo, this.bpm + 1)); }
  decreaseBPM() { this.setBPM(Math.max(1, this.bpm - 1)); }

  onMIDITempoTap() {
    const now = performance.now();
    this.tapTimes.push(now);
    if (this.tapTimes.length > 4) this.tapTimes.shift();

    if (this.tapTimes.length >= 2) {
      const intervals = [];
      for (let i = 1; i < this.tapTimes.length; i++) {
        const interval = this.tapTimes[i] - this.tapTimes[i - 1];
        if (interval >= 100 && interval <= 10000) intervals.push(interval);
      }
      if (intervals.length >= 1) {
        const avg = intervals.reduce((s, i) => s + i, 0) / intervals.length;
        if (avg > 0 && isFinite(avg)) {
          const newBPM = 60000 / avg;
          if (newBPM >= MIDI_CONSTANTS.clock.minTempo && newBPM <= MIDI_CONSTANTS.clock.maxTempo) {
            this.setBPM(newBPM);
          }
        }
      }
    }
  }

  startInternalClock() {
    this._musicalHoldBeats = null;
    this.isPlaying = true;
    this.isStopped = false;
    this.isPaused = false;
    this.clockSource = 'internal';
    this._updateTransportState();
  }

  stopInternalClock() {
    if (this.isPlaying && this.clockSource === 'internal') {
      this._musicalHoldBeats = this.animationTime * (this.bpm / 60);
    }
    this.isPlaying = false;
    this.isStopped = true;
    this.isPaused = false;
    this._updateTransportState();
  }

  resetClock() {
    this._musicalHoldBeats = null;
    this.clockPulses = 0;
    this.animationTime = 0;
    this._bpmTickCount = 0;
    this._updateTransportState();
  }

  getAnimationTime() { return this.animationTime; }

  updateAnimationTime(deltaTime) {
    if (this.isPlaying) {
      this.animationTime += deltaTime;
    }
  }

  /**
   * Musical position in quarter-note beats (continuous float).
   * Stable while stopped: BPM can jitter from MIDI analysis without moving phase.
   */
  getMusicalBeats() {
    if (this._musicalHoldBeats != null) {
      return this._musicalHoldBeats;
    }
    if (this.isPlaying && this.clockSource === 'external' && this.isClockActive) {
      return this.clockPulses / MIDI_CONSTANTS.clock.pulsesPerQuarterNote;
    }
    return this.animationTime * (this.bpm / 60);
  }

  /** Wall-time seconds along the musical timeline (beat count × current beat length). */
  getMusicalTimeSeconds() {
    return this.getMusicalBeats() * (60 / this.bpm);
  }

  /** @deprecated Use getMusicalTimeSeconds() */
  getClockTimeInSeconds() {
    return this.getMusicalTimeSeconds();
  }

  _updateTransportState() {
    this.state.set('transportPlaying', this.isPlaying);
    this.state.set('transportStopped', this.isStopped);
    this.state.set('clockSource', this.clockSource);
  }

  _updateBPMDisplay() {
    const el = document.getElementById('bpm-display');
    if (el) el.textContent = `${Math.round(this.bpm)} BPM`;
  }

  initializeFromState() {
    const stateBPM = this.state.get('globalBPM');
    if (stateBPM) this.setBPM(stateBPM);
  }
}
